import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogActions,
  Skeleton,
  Chip,
} from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import { useAuth } from "../context/AuthContext";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import LoadingPage from "./LoadingPage";

// Fix Leaflet marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const EditDevice = () => {
  const { accessToken, isAdmin, isSuperuser } = useAuth();
  const { id } = useParams(); // Get deviceId from URL
  const navigate = useNavigate();
  const baseURL = process.env.REACT_APP_BASEURL;
  const mapRef = useRef(null);

  // State
  const [serialNumber, setSerialNumber] = useState("");
  const [name, setName] = useState("");
  const [soilType, setSoilType] = useState("Loam");
  const [soilDryBulkDensity, setSoilDryBulkDensity] = useState("1.4");
  const [fieldCapacity, setFieldCapacity] = useState("25");
  const [wiltingPoint, setWiltingPoint] = useState("8");
  const [irrigationThreshold, setIrrigationThreshold] = useState("0.5");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [depth, setDepth] = useState("");
  const [selectedGroup, setSelectedGroup] = useState([]);
  const [siteName, setSiteName] = useState("");
  const [siteId, setSiteId] = useState("");
  const [groups, setGroups] = useState([]);
  const [mapCenter, setMapCenter] = useState([0, 0]);
  const [isValidCoordinates, setIsValidCoordinates] = useState(true);
  const [mapEnabled, setMapEnabled] = useState(false);
  const [loadingDevice, setLoadingDevice] = useState(true);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogAction, setDialogAction] = useState("");
  const [dialogMessage, setDialogMessage] = useState("");

  // Satellite tile layer
  const tileLayer = {
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution:
      'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
  };

  // Fetch device and groups data
  useEffect(() => {
    if (!id) {
      setError("Invalid device ID. Please select a valid device.");
      setLoadingDevice(false);
      setLoadingGroups(false);
      return;
    }

    if (!isAdmin && !isSuperuser) {
      setError("You do not have permission to edit devices.");
      setLoadingDevice(false);
      setLoadingGroups(false);
      return;
    }

    const fetchData = async () => {
      setLoadingDevice(true);
      setLoadingGroups(true);
      setError(null);

      try {
        // Fetch device data
        const deviceResponse = await axios.get(`${baseURL}/devices/${id}/?with_details=true`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const deviceData = deviceResponse.data;
        console.log(deviceData);
        setSerialNumber(deviceData.serial_number || "");
        setName(deviceData.name || "");
        setSoilType(deviceData.location?.soil_type || "Loam");
        setSoilDryBulkDensity(deviceData.location?.soil_density?.toString() || "1.4");
        setFieldCapacity(deviceData.location?.field_capacity?.toString() || "25");
        setWiltingPoint(deviceData.location?.wilting_point?.toString() || "8");
        setIrrigationThreshold(deviceData.location?.irrigation_threshold?.toString() || "0.5");
        setLatitude(deviceData.location?.latitude?.toString() || "");
        setLongitude(deviceData.location?.longitude?.toString() || "");
        setDepth(deviceData.location?.height ? Math.abs(deviceData.location.height * 100).toString() : "");
        setSelectedGroup(deviceData.groups || []);
        setSiteId(deviceData.site || "");
        setMapCenter([
          parseFloat(deviceData.location?.latitude) || 0,
          parseFloat(deviceData.location?.longitude) || 0,
        ]);
        setMapEnabled(!!deviceData.location?.latitude && !!deviceData.location?.longitude);

        // Fetch site details for name
        const siteResponse = await axios.get(`${baseURL}/sites/${deviceData.site}/?with_image=true`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        setSiteName(siteResponse.data.name || `Site ${deviceData.site}`);

        // Fetch groups for the site
        const groupsResponse = await axios.get(`${baseURL}/groups/?site=${deviceData.site}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        setGroups(groupsResponse.data || []);
      } catch (err) {
        if (err.response) {
          setError(err.response.data.message || "Failed to load device or groups. Please try again.");
        } else if (err.request) {
          setError("No response from server. Please try again.");
        } else {
          setError("An unexpected error occurred. Please try again.");
        }
      } finally {
        setLoadingDevice(false);
        setLoadingGroups(false);
      }
    };
    fetchData();
  }, [accessToken, baseURL, id, isAdmin, isSuperuser]);

  // Validate coordinates
  useEffect(() => {
    const lat = parseFloat(latitude);
    const lon = parseFloat(longitude);
    const isValidLat = !isNaN(lat) && lat >= -90 && lat <= 90;
    const isValidLon = !isNaN(lon) && lon >= -180 && lon <= 180;
    setIsValidCoordinates(isValidLat && isValidLon);
    if (isValidLat && isValidLon) {
      setMapCenter([lat, lon]);
    }
  }, [latitude, longitude]);

  // Map component to update coordinates on click
  const MapUpdater = ({ center, onClick }) => {
    const map = useMap();
    useEffect(() => {
      if (center[0] !== 0 && center[1] !== 0 && mapEnabled) {
        map.setView(center, 15);
      }
    }, [center, map, mapEnabled]);

    useEffect(() => {
      const handleClick = (e) => {
        const { lat, lng } = e.latlng;
        setLatitude(lat.toFixed(6));
        setLongitude(lng.toFixed(6));
        onClick([lat, lng]);
      };
      map.on("click", handleClick);
      return () => map.off("click", handleClick);
    }, [map, onClick]);

    return null;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Validate inputs
    if (!serialNumber.trim()) {
      setError("Serial number is required.");
      return;
    }
    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    if (!latitude || !longitude || !isValidCoordinates) {
      setError("Valid latitude and longitude are required.");
      return;
    }
    if (!siteId) {
      setError("Site ID is missing.");
      return;
    }

    // Open confirmation dialog for update
    setDialogAction("update");
    setDialogMessage(`Are you sure you want to update the device "${name}"?`);
    setDialogOpen(true);
  };

  // Perform the update action
  const performUpdate = async () => {
    setLoadingSubmit(true);
    try {
      const deviceData = {
        serial_number: Number(serialNumber),
        name,
        location: {
          soil_type: soilType,
          soil_density: parseFloat(soilDryBulkDensity),
          field_capacity: parseFloat(fieldCapacity),
          wilting_point: parseFloat(wiltingPoint),
          irrigation_threshold: parseFloat(irrigationThreshold),
          latitude: parseFloat(latitude),
          longitude: parseFloat(longitude),
          height: depth ? parseFloat(depth) / -100 : null,
        },
        groups: selectedGroup.length > 0 ? selectedGroup.map((groupId) => Number(groupId)) : [],
        site: Number(siteId),
      };
      const response = await axios.patch(`${baseURL}/devices/${id}`, deviceData, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      });
      setSuccess("Device updated successfully!");
      setSelectedGroup(response.data.groups || []);
    } catch (err) {
      if (err.response) {
        setError(err.response.data.message || "Failed to update device. Please try again.");
      } else if (err.request) {
        setError("No response from server. Please try again.");
      } else {
        setError("An unexpected error occurred. Please try again.");
      }
    } finally {
      setLoadingSubmit(false);
    }
  };

  // Handle device deletion
  const handleDelete = () => {
    setError(null);
    setSuccess(null);
    setDialogAction("delete");
    setDialogMessage(`Are you sure you want to delete the device "${name}"? This action cannot be undone.`);
    setDialogOpen(true);
  };

  // Handle dialog confirmation
  const handleConfirmAction = async () => {
    setDialogOpen(false);
    if (dialogAction === "update") {
      await performUpdate();
    } else if (dialogAction === "delete") {
      setLoadingDelete(true);
      try {
        await axios.delete(`${baseURL}/devices/${id}`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            accept: "application/json",
          },
        });
        setSuccess("Device deleted successfully!");
        setTimeout(() => navigate("/dashboard/devices"), 1500); // Redirect after brief success message
      } catch (err) {
        if (err.response) {
          setError(err.response.data.message || "Failed to delete device. Please try again.");
        } else if (err.request) {
          setError("No response from server. Please try again.");
        } else {
          setError("An unexpected error occurred. Please try again.");
        }
      } finally {
        setLoadingDelete(false);
      }
    }
  };

  // Handle dialog cancellation
  const handleCancelAction = () => {
    setDialogOpen(false);
    setDialogAction("");
    setDialogMessage("");
  };

  // Loading skeleton
  const LoadingSkeleton = () => (
    <Container maxWidth="md" sx={{ py: { xs: 2, sm: 3 } }}>
      <Typography variant="h4" gutterBottom>
        <Skeleton width="30%" height={40} />
      </Typography>
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Skeleton variant="rectangular" width="100%" height={56} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" width="100%" height={56} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" width="100%" height={300} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" width="100%" height={56} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" width={120} height={40} />
      </Paper>
    </Container>
  );

  // Redirect or show error if id is invalid or no permission
  if (!id || (!isAdmin && !isSuperuser)) {
    return (
      <Container maxWidth="md" sx={{ py: { xs: 2, sm: 3 } }}>
        <Alert severity="error">
          {id ? "You do not have permission to edit devices." : "Invalid device ID. Please select a valid device or return to the device list."}
        </Alert>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate("/dashboard/devices")}
          sx={{ mt: 2 }}
        >
          Back to Devices
        </Button>
      </Container>
    );
  }

  if (loadingDevice || loadingGroups) {
    return <LoadingPage />;
  }

  return (
    <Container maxWidth="md" sx={{ py: { xs: 2, sm: 3 } }}>
      <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: "1.75rem", md: "2.125rem" } }}>
        Edit Device
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
        <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Site (Read-only) */}
          <TextField
            label="Site"
            value={siteName}
            fullWidth
            variant="outlined"
            disabled
            sx={{ "& .MuiInputLabel-root": { color: "black", fontWeight: 100 } }}
          />
          {/* Serial Number and Name */}
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Serial Number"
              value={serialNumber}
              onChange={(e) => setSerialNumber(e.target.value)}
              fullWidth
              required
              variant="outlined"
            />
            <TextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              required
              variant="outlined"
            />
          </Box>
          {/* Soil Spec */}
          <Typography variant="h6" sx={{ fontSize: { xs: "1.1rem", sm: "1.25rem" } }}>
            Soil Specifications
          </Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="soil-type-select-label">Soil Type</InputLabel>
              <Select
                labelId="soil-type-select-label"
                value={soilType}
                onChange={(e) => setSoilType(e.target.value)}
                label="Soil Type"
              >
                {["Loam", "Clay", "Sand USGA", "Organic"].map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Soil Dry Bulk Density (kg/l)"
              value={soilDryBulkDensity}
              onChange={(e) => setSoilDryBulkDensity(e.target.value)}
              fullWidth
              type="number"
              slotProps={{
                input: {
                  inputProps: { step: 0.2, min: 0 },
                },
              }}
              variant="outlined"
            />
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Field Capacity (%)"
              value={fieldCapacity}
              onChange={(e) => setFieldCapacity(e.target.value)}
              fullWidth
              type="number"
              slotProps={{
                input: {
                  inputProps: { step: 1, min: 0 },
                },
              }}
              variant="outlined"
            />
            <TextField
              label="Wilting Point (%)"
              value={wiltingPoint}
              onChange={(e) => setWiltingPoint(e.target.value)}
              fullWidth
              type="number"
              slotProps={{
                input: {
                  inputProps: { step: 1, min: 0 },
                },
              }}
              variant="outlined"
            />
            <TextField
              label="Irrigation Threshold"
              value={irrigationThreshold}
              onChange={(e) => setIrrigationThreshold(e.target.value)}
              fullWidth
              type="number"
              slotProps={{
                input: {
                  inputProps: { step: 0.01, min: 0 },
                },
              }}
              variant="outlined"
            />
          </Box>
          {/* Location */}
          <Typography variant="h6" sx={{ fontSize: { xs: "1.1rem", sm: "1.25rem" } }}>
            Location
          </Typography>
          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <TextField
              label="Latitude"
              value={latitude}
              onChange={(e) => setLatitude(e.target.value)}
              fullWidth
              type="text"
              required
              error={!isValidCoordinates && latitude !== ""}
              helperText={
                !isValidCoordinates && latitude !== "" ? "Latitude must be between -90 and 90" : ""
              }
            />
            <TextField
              label="Longitude"
              value={longitude}
              onChange={(e) => setLongitude(e.target.value)}
              fullWidth
              type="text"
              required
              error={!isValidCoordinates && longitude !== ""}
              helperText={
                !isValidCoordinates && longitude !== "" ? "Longitude must be between -180 and 180" : ""
              }
            />
          </Box>
          <Box sx={{ height: 300, border: "1px solid #ddd", mb: 2 }}>
            {mapEnabled && isValidCoordinates && latitude && longitude ? (
              <div ref={mapRef} style={{ height: "100%", width: "100%" }}>
                <MapContainer center={mapCenter} zoom={15} style={{ height: "100%", width: "100%" }}>
                  <TileLayer url={tileLayer.url} attribution={tileLayer.attribution} />
                  <TileLayer
                    url="https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
                    attribution=""
                  />
                  <Marker position={mapCenter} />
                  <MapUpdater center={mapCenter} onClick={setMapCenter} />
                </MapContainer>
              </div>
            ) : (
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "grey.100",
                  cursor: "pointer",
                }}
                onClick={() => setMapEnabled(true)}
              >
                <Typography color="text.secondary">
                  {latitude && longitude ? "Click the map to set coordinates" : "Click to enable map"}
                </Typography>
              </Box>
            )}
          </Box>
          {/* Depth */}
          <TextField
            label="Depth (cm)"
            value={depth}
            onChange={(e) => setDepth(e.target.value)}
            fullWidth
            type="number"
            slotProps={{
              input: {
                inputProps: { step: 1, min: 0 },
              },
            }}
            variant="outlined"
          />
          {/* Groups Dropdown */}
          <FormControl fullWidth variant="outlined" disabled={loadingGroups || !siteId}>
            <InputLabel id="group-select-label">Group (optional)</InputLabel>
            <Select
              labelId="group-select-label"
              value={selectedGroup}
              onChange={(e) => setSelectedGroup(e.target.value)}
              label="Group (optional)"
              multiple
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((groupId) => {
                    const group = groups.find((g) => g.id === groupId);
                    return <Chip key={groupId} label={group ? (group.name || `Group ${groupId}`) : `Group ${groupId}`} size="medium" variant="outlined" deleteIcon={<DeleteIcon />}  onDelete={() => setSelectedGroup((prev) => prev.filter((d) => d !== groupId))}
                                sx={{
                                    backgroundColor: "transparent",
                                    color: "#f5c518",
                                    fontSize: '16px',
                                    fontWeight: 600,
                                    border: "1px solid #f5c518",
                                    borderRadius: '5px',
                                    "& .MuiChip-deleteIcon": {
                                    color: "#d32f2f", // delete icon color
                                    },
                                    "&:hover": {
                                    backgroundColor: "#d32f2f",
                                    color: "white",
                                    border: "1px solid red",
                                    "& .MuiChip-deleteIcon": {
                                    color: "white", // delete icon color
                                    },
                                    },
                                }}
                    />;
                  })}
                </Box>
              )}
            >
              {loadingGroups ? (
                <MenuItem value="" disabled>
                  Loading groups...
                </MenuItem>
              ) : groups.length === 0 ? (
                <MenuItem value="" disabled>
                  No groups available
                </MenuItem>
              ) : (
                groups.map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    {group.name || `Group ${group.id}`}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          {/* Action Buttons */}
          <Box sx={{ display: "flex", gap: 2, mt: 2, alignSelf: "flex-end" }}>
            <Button
              variant="contained"
              color="error"
              onClick={handleDelete}
              disabled={loadingDelete || loadingSubmit || loadingDevice || loadingGroups}
              sx={{ minWidth: 120 }}
            >
              {loadingDelete ? <CircularProgress size={24} /> : "Delete Device"}
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loadingSubmit || loadingDelete || loadingDevice || loadingGroups}
              sx={{ minWidth: 120 }}
            >
              {loadingSubmit ? <CircularProgress size={24} /> : "Update Device"}
            </Button>
          </Box>
        </Box>
      </Paper>
      {/* Confirmation Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCancelAction}
        aria-describedby="alert-dialog-description"
      >
        <DialogContent>
          <DialogContentText id="alert-dialog-description" sx={{ color: "black" }}>
            {dialogMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelAction} color="primary">
            No
          </Button>
          <Button onClick={handleConfirmAction} color="error" autoFocus>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EditDevice;
